import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useFiltersStorage } from './persistence';
import {
  FilteringContextValue,
  FilterDefinitions,
  ResolvedFilters,
  FilterUpdateFn,
} from './types';

export const FilterContext =
  createContext<FilteringContextValue<FilterDefinitions> | null>(null);

// There is no guarantee that the T of the context will match the T of this function.
export const useFilteringMaybe = <
  T extends FilterDefinitions,
>(): FilteringContextValue<T> | null =>
  useContext(FilterContext) as FilteringContextValue<T> | null;

// There is no guarantee that the T of the context will match the T of this function.
export const useFiltering = <
  T extends FilterDefinitions,
>(): FilteringContextValue<T> => {
  const context = useFilteringMaybe();
  if (!context) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }
  return context as FilteringContextValue<T>;
};

export type FilterProviderProps<T extends FilterDefinitions> = {
  definitions: T;
  searchTermKey: string | null;
  /**
   * By default, a persistance key will be generated from the first path segment
   * of the URL and the keys of all the filter definitions.
   *
   * If you wish to use a specific key, you can pass it here.
   *
   * This is useful in cases where different routes under the same basename use
   * the identically named filters definitions but with different possible values.
   *
   * Can be set to `null` to disable persistance.
   */
  persistanceKey?: string | null;
  children: ReactNode;
};

/**
 * If you need a FilterProvider that doesn't store state in the URL,
 * use ScopedFilterProvider instead.
 */
export const FilterProvider = <T extends FilterDefinitions>({
  definitions,
  searchTermKey,
  persistanceKey,
  children,
}: FilterProviderProps<T>): ReactElement => {
  const { filters, setFilters, searchTerm, setSearchTerm, forcedAt } =
    useFiltersStorage(definitions, searchTermKey, persistanceKey);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters({ [key]: null });
    },
    [setFilters],
  );

  const value: FilteringContextValue<T> = useMemo(
    () => ({
      definitions,
      filters,
      forcedAt,
      setFilters,
      removeFilter,
      searchTerm,
      setSearchTerm,
      searchDisabled: !searchTermKey,
    }),
    [filters, forcedAt, setFilters, removeFilter],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

/**
 * Works the same as FilterProvider, but stores state locally rather than in the URL.
 */
export const ScopedFilterProvider = <T extends FilterDefinitions>({
  definitions,
  children,
}: Omit<FilterProviderProps<T>, 'searchTermKey'>): ReactElement => {
  const [filters, setFiltersRaw] = useState<ResolvedFilters<T>>(() => {
    const initialFilters: ResolvedFilters<T> = {} as ResolvedFilters<T>;
    for (const key in definitions) {
      const { label, defaultValue } = definitions[key];
      if (defaultValue) {
        initialFilters[key] = {
          label,
          value: defaultValue,
        };
      }
    }
    return initialFilters;
  });
  const [searchTerm, setSearchTermRaw] = useState('');
  const [forcedAt, setForcedAt] = useState(0);

  const removeFilter = useCallback(
    (key: string) => {
      setFiltersRaw((prev) => ({ ...prev, [key]: null }));
    },
    [setFiltersRaw],
  );

  const setFilters: FilterUpdateFn = useCallback(
    (filters) => setFiltersRaw((prev) => ({ ...prev, ...filters })),
    [setFiltersRaw],
  );

  const setSearchTerm = useCallback(
    (searchTerm: string | null, forcedAt?: number) => {
      setSearchTermRaw(searchTerm ?? '');
      if (forcedAt) {
        setForcedAt(forcedAt);
      }
    },
    [],
  );

  const value: FilteringContextValue<T> = useMemo(
    () => ({
      definitions,
      filters,
      forcedAt,
      setFilters,
      removeFilter,
      searchTerm,
      setSearchTerm,
      searchDisabled: false,
    }),
    [filters, setFilters, searchTerm, setSearchTerm],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};
